import { useState, useEffect, useCallback } from 'react';
import styles from './EditorOnboarding.module.css';

const STORAGE_KEY = 'locasmart_editor_onboarding_seen';

interface Step {
  target: string;
  title: string;
  description: string;
}

const STEPS: Step[] = [
  {
    target: '[data-tour="toolbar"]',
    title: 'Barra de Formatação',
    description:
      'Use os botões para formatar o texto: negrito, itálico, alinhamento, listas e mais.',
  },
  {
    target: '[data-tour="placeholders"]',
    title: 'Painel de Variáveis',
    description:
      'Clique em uma variável para inserir no documento. Elas serão substituídas automaticamente ao gerar o contrato.',
  },
  {
    target: '[data-tour="editor-area"]',
    title: 'Área de Edição',
    description:
      'Edite o conteúdo do contrato diretamente. Posicione o cursor onde deseja inserir variáveis.',
  },
  {
    target: '[data-tour="save-button"]',
    title: 'Salvar Documento',
    description:
      'Quando terminar, clique aqui para salvar o modelo de contrato.',
  },
];

interface EditorOnboardingProps {
  active?: boolean;
  onFinish?: () => void;
}

export function EditorOnboarding({ active, onFinish }: EditorOnboardingProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [spotlight, setSpotlight] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (active) {
      setCurrentStep(0);
      setIsVisible(true);
      return;
    }

    const seen = localStorage.getItem(STORAGE_KEY);
    if (!seen) {
      // Pequeno delay para o DOM estar pronto
      const timer = setTimeout(() => setIsVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, [active]);

  const updateSpotlight = useCallback(() => {
    if (!isVisible) return;

    const step = STEPS[currentStep];
    const el = document.querySelector(step.target);
    if (el) {
      setSpotlight(el.getBoundingClientRect());
    } else {
      setSpotlight(null);
    }
  }, [currentStep, isVisible]);

  useEffect(() => {
    updateSpotlight();
    window.addEventListener('resize', updateSpotlight);
    return () => window.removeEventListener('resize', updateSpotlight);
  }, [updateSpotlight]);

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      handleFinish();
    }
  };

  const handleSkip = () => {
    handleFinish();
  };

  const handleFinish = () => {
    setIsVisible(false);
    localStorage.setItem(STORAGE_KEY, 'true');
    onFinish?.();
  };

  if (!isVisible) return null;

  const step = STEPS[currentStep];
  const isLast = currentStep === STEPS.length - 1;

  // Posicionamento inteligente do tooltip
  const tooltipStyle: React.CSSProperties = {};
  const TOOLTIP_WIDTH = 340;
  const TOOLTIP_HEIGHT_ESTIMATE = 180;
  const GAP = 12;

  if (spotlight) {
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    const fitsBelow = spotlight.bottom + GAP + TOOLTIP_HEIGHT_ESTIMATE < viewportH;
    const fitsRight = spotlight.right + GAP + TOOLTIP_WIDTH < viewportW;

    if (fitsBelow) {
      // Posicionar abaixo
      tooltipStyle.top = spotlight.bottom + GAP;
      tooltipStyle.left = Math.min(
        Math.max(GAP, spotlight.left),
        viewportW - TOOLTIP_WIDTH - GAP
      );
    } else if (fitsRight) {
      // Posicionar à direita (para elementos altos como o painel de variáveis)
      tooltipStyle.top = Math.min(
        Math.max(GAP, spotlight.top),
        viewportH - TOOLTIP_HEIGHT_ESTIMATE - GAP
      );
      tooltipStyle.left = spotlight.right + GAP;
    } else {
      // Fallback: posicionar acima
      tooltipStyle.bottom = viewportH - spotlight.top + GAP;
      tooltipStyle.left = Math.min(
        Math.max(GAP, spotlight.left),
        viewportW - TOOLTIP_WIDTH - GAP
      );
    }
  } else {
    tooltipStyle.top = '50%';
    tooltipStyle.left = '50%';
    tooltipStyle.transform = 'translate(-50%, -50%)';
  }

  return (
    <div className={`${styles.overlay} ${!spotlight ? styles.overlayDark : ''}`}>
      {/* Spotlight cutout via box-shadow */}
      {spotlight && (
        <div
          className={styles.spotlight}
          style={{
            top: spotlight.top - 4,
            left: spotlight.left - 4,
            width: spotlight.width + 8,
            height: spotlight.height + 8,
          }}
        />
      )}

      {/* Tooltip */}
      <div className={styles.tooltip} style={tooltipStyle}>
        <div className={styles.tooltipHeader}>
          <span className={styles.stepCounter}>
            {currentStep + 1} de {STEPS.length}
          </span>
          <button className={styles.skipButton} onClick={handleSkip}>
            Pular
          </button>
        </div>
        <h3 className={styles.tooltipTitle}>{step.title}</h3>
        <p className={styles.tooltipDescription}>{step.description}</p>
        <button className={styles.nextButton} onClick={handleNext}>
          {isLast ? 'Concluir' : 'Próximo'}
        </button>
      </div>
    </div>
  );
}
